import { __decorate } from "tslib";
import { AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { PaymentCustomer } from '../PaymentCustomer.js';
import { downgradePaymentMethodV150, PaymentMethod, PaymentMethodHelper, PaymentMethodV150 } from '../PaymentMethod.js';
import { PaymentProvider } from '../PaymentProvider.js';
import { PaymentStatus } from '../PaymentStatus.js';
import { PaymentType, PaymentTypeHelper } from '../PaymentType.js';
import { TransferSettings } from '../webshops/TransferSettings.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
export class Payment extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.type = PaymentType.Payment;
        this.status = PaymentStatus.Created;
        this.provider = null;
        this.customer = null;
        this.price = 0;
        this.freeContribution = null;
        // Transfer description if paid via transfer
        this.transferDescription = null;
        this.transferSettings = null;
        this.paidAt = null;
        this.createdAt = new Date();
        this.updatedAt = new Date();
        this.organizationId = null;
        this.payingOrganizationId = null;
        this.invoiceId = null;
        this.payingUserId = null;
    }
    get isPending() {
        return this.status !== PaymentStatus.Succeeded && this.status !== PaymentStatus.Failed;
    }
    get isOverDue() {
        const daysToPay = 7;
        return this.isPending && this.createdAt < new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * daysToPay);
    }
    get isSucceeded() {
        return this.status === PaymentStatus.Succeeded;
    }
    get isFailed() {
        return this.status === PaymentStatus.Failed;
    }
    get canChangeStatus() {
        return this.method === PaymentMethod.Transfer || this.method === PaymentMethod.PointOfSale || this.method === PaymentMethod.Unknown;
    }
    get title() {
        if (this.method !== PaymentMethod.Unknown) {
            if (this.type !== PaymentType.Payment) {
                return $t(`e13af8ee-f442-4a98-b3e6-6f3d84fba30c`) + ' ' + PaymentMethodHelper.getName(this.method);
            }
            return PaymentMethodHelper.getNameCapitalized(this.method);
        }
        return Formatter.capitalizeFirstLetter(PaymentTypeHelper.getName(this.type));
    }
    get theme() {
        if (this.type === PaymentType.Reallocation) {
            return 'theme-secundary';
        }
        if (this.type === PaymentType.Chargeback || this.type === PaymentType.Refund) {
            return 'theme-error';
        }
    }
    getHTMLTable() {
        var _a, _b, _c, _d, _e, _f;
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
        /**
         * Replacement.create({
                    token: "priceToPay",
                    value: forcePayment?.status !== PaymentStatus.Succeeded ? Formatter.price(forcePayment?.price ?? 0) : ""
                }),
                Replacement.create({
                    token: "paymentMethod",
                    value: forcePayment?.method ? PaymentMethodHelper.getName(forcePayment.method) : PaymentMethodHelper.getName(this.data.paymentMethod)
                }),
                Replacement.create({
                    token: "transferDescription",
                    value: forcePayment?.status !== PaymentStatus.Succeeded && forcePayment?.method === PaymentMethod.Transfer ? (forcePayment?.transferDescription ?? "") : ""
                }),
                Replacement.create({
                    token: "transferBankAccount",
                    value: forcePayment?.status !== PaymentStatus.Succeeded && forcePayment?.method === PaymentMethod.Transfer ? ((webshop?.meta.transferSettings.iban ? webshop?.meta.transferSettings.iban : organization.meta.transferSettings.iban) ?? "") : ""
                }),
                Replacement.create({
                    token: "transferBankCreditor",
                    value: forcePayment?.status !== PaymentStatus.Succeeded && forcePayment?.method === PaymentMethod.Transfer ? ((webshop?.meta.transferSettings.creditor ? webshop?.meta.transferSettings.creditor : organization.meta.transferSettings.creditor) ?? organization.name) : ""
                }),
         */
        const data = [
            {
                title: $t(`07e7025c-0bfb-41be-87bc-1023d297a1a2`),
                value: this.method ? Formatter.capitalizeFirstLetter(PaymentMethodHelper.getName(this.method)) : '',
            },
            {
                title: $t(`18aed6d0-0880-4d06-9260-fe342e6e8064`),
                value: this.status !== PaymentStatus.Succeeded ? Formatter.price((_a = this === null || this === void 0 ? void 0 : this.price) !== null && _a !== void 0 ? _a : 0) : Formatter.price(0),
            },
            ...(this.method === PaymentMethod.Transfer
                ? [
                    {
                        title: $t(`136b7ba4-7611-4ee4-a46d-60758869210f`),
                        value: (_b = this.transferDescription) !== null && _b !== void 0 ? _b : '',
                    },
                    {
                        title: $t(`1fbed7d4-9e6e-4c87-b7fe-a9059aef2492`),
                        value: (_d = (_c = this.transferSettings) === null || _c === void 0 ? void 0 : _c.iban) !== null && _d !== void 0 ? _d : '',
                    },
                    {
                        title: $t(`31c28f13-d3b8-42ee-8979-c8224633237e`),
                        value: (_f = (_e = this.transferSettings) === null || _e === void 0 ? void 0 : _e.creditor) !== null && _f !== void 0 ? _f : '',
                    },
                ]
                : []),
        ];
        for (const replacement of data) {
            if (replacement.value.length == 0) {
                continue;
            }
            str += `<tr><td><h4>${Formatter.escapeHtml(replacement.title)}</h4></td><td>${Formatter.escapeHtml(replacement.value)}</td></tr>`;
        }
        return str + '</tbody></table>';
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Payment.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentType), version: 353 })
], Payment.prototype, "type", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethodV150), nullable: true }),
    field({
        decoder: new EnumDecoder(PaymentMethod),
        version: 151,
        downgrade: downgradePaymentMethodV150,
    })
], Payment.prototype, "method", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentStatus) })
], Payment.prototype, "status", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentProvider), nullable: true, version: 152 })
], Payment.prototype, "provider", void 0);
__decorate([
    field({ decoder: PaymentCustomer, nullable: true, version: 321 })
], Payment.prototype, "customer", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Payment.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 92 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], Payment.prototype, "freeContribution", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Payment.prototype, "transferDescription", void 0);
__decorate([
    field({ decoder: TransferSettings, nullable: true, version: 160 })
], Payment.prototype, "transferSettings", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Payment.prototype, "paidAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Payment.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Payment.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 324 })
], Payment.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 344 })
], Payment.prototype, "payingOrganizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 392 })
], Payment.prototype, "invoiceId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 393 })
], Payment.prototype, "payingUserId", void 0);
export class Settlement extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Fee for the payment provider for the individual payment
         * Only set if it is witheld from the settlement/payout
         */
        this.fee = 0;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], Settlement.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Settlement.prototype, "reference", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Settlement.prototype, "settledAt", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Settlement.prototype, "amount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 195 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Settlement.prototype, "fee", void 0);
export class PrivatePayment extends Payment {
    constructor() {
        super(...arguments);
        this.settlement = null;
        this.iban = null;
        this.ibanName = null;
        this.transferFee = 0;
        this.serviceFeePayout = 0;
        this.serviceFeeManual = 0;
        this.serviceFeeManualCharged = 0;
    }
}
__decorate([
    field({ decoder: Settlement, nullable: true })
], PrivatePayment.prototype, "settlement", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 153 })
], PrivatePayment.prototype, "iban", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 153 })
], PrivatePayment.prototype, "ibanName", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 197 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], PrivatePayment.prototype, "transferFee", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PrivatePayment.prototype, "serviceFeePayout", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PrivatePayment.prototype, "serviceFeeManual", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PrivatePayment.prototype, "serviceFeeManualCharged", void 0);
//# sourceMappingURL=Payment.js.map